import React, { useState, MouseEvent } from 'react';
import moment, { Moment } from 'moment';
import {
	Button,
	Grid,
	IconButton,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	Popper,
	createSvgIcon,
	useTheme,
} from '@mui/material';
import { CalendarPicker } from '@mui/x-date-pickers';
import CAppPaper from '../../../../app/components/CAppPaper';
import { useTranslation } from 'react-i18next';
import langInvestDateRangePicker from '../../langs/langInvestDateRangePicker';

type TListItemsDate = '1_month' | '3_months' | '6_months' | '1_year' | '3_years' | '5_years' | 'custom' | 'all';
type TDateSelection = {
	tag: TListItemsDate;
	moment: [number, moment.unitOfTime.DurationConstructor];
};
const dateSelection: TDateSelection[] = [
	{ tag: '1_month', moment: [1, 'months'] },
	{ tag: '3_months', moment: [3, 'months'] },
	{ tag: '6_months', moment: [6, 'months'] },
	{ tag: '1_year', moment: [1, 'years'] },
	{ tag: '3_years', moment: [3, 'years'] },
	{ tag: '5_years', moment: [5, 'years'] },
];

const getCurrentDateSelectionTag = (currentDate: Moment, fromDate: Moment): TListItemsDate => {
	for (const item of dateSelection) {
		if (
			fromDate
				.clone()
				.subtract(...item.moment)
				.isSame(currentDate)
		)
			return item.tag;
	}
	return 'custom';
};

type CDateRangePickerPopperProps = {
	startDate: Moment;
	endDate: Moment;
	onChange: (newStartDate: Moment, newEndDate: Moment) => void;
	onClose: (event: React.MouseEvent<HTMLElement>) => void;
	minDate: Moment;
	maxDate: Moment;
};
const CDateRangePickerPopper = (props: CDateRangePickerPopperProps) => {
	const { t } = useTranslation();
	const { startDate, endDate, onChange, onClose, minDate, maxDate } = props;
	const [tempStartDate, setTempStartDate] = useState(moment.max(startDate, minDate));
	const [tempEndDate, setTempEndDate] = useState(moment.min(endDate, maxDate));
	const [itemDateSelected, setItemDateSelected] = useState<TListItemsDate>(
		getCurrentDateSelectionTag(startDate, tempEndDate),
	);
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';

	const onItemRangeSelected = (selected: TDateSelection) => {
		setItemDateSelected(selected.tag);
		setTempStartDate(moment.max(endDate.clone().subtract(...selected.moment), minDate));
		setTempEndDate(moment.min(endDate, maxDate));
	};

	const onItemAllSelected = () => {
		setItemDateSelected('all');
		setTempStartDate(minDate);
		setTempEndDate(maxDate);
	};

	const onSelectCustom = (cursor: 'start' | 'end', date: Moment) => {
		setItemDateSelected('custom');
		if (cursor === 'start') setTempStartDate(date);
		else setTempEndDate(date);
	};

	const onSubmit = (event: React.MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
		onChange(tempStartDate, tempEndDate);
		onClose(event);
	};
	return (
		<CAppPaper sx={{ padding: '10px', maxWidth: '850px' }}>
			<Grid container>
				<Grid item sm={3}>
					<Grid container>
						<List
							sx={{
								width: '100%',
								borderRight: '1px solid #BDBDBD80',
								paddingRight: '1rem',
								'& .Mui-selected': {
									backgroundColor: '#2ABDBB !important',
									color: '#FFFFFF',
									fontWeight: 600,
									borderRadius: '4px',
									':hover': {
										backgroundColor: '#008786',
									},
								},
								'& .MuiListItem-root': {
									':hover': {
										borderRadius: '4px',
										backgroundColor: '#E6F4F1',
									},
								},
							}}
							aria-label="contacts"
						>
							{dateSelection.map((item) => (
								<ListItem disablePadding key={item.tag}>
									<ListItemButton
										onClick={() => onItemRangeSelected(item)}
										selected={itemDateSelected === item.tag}
									>
										<ListItemText
											primary={t(
												item.moment[1] === 'years'
													? langInvestDateRangePicker.x_years.tag
													: langInvestDateRangePicker.x_months.tag,
												{ count: item.moment[0] as number },
											)}
										/>
									</ListItemButton>
								</ListItem>
							))}
							<ListItem disablePadding>
								<ListItemButton selected={itemDateSelected === 'custom'}>
									<ListItemText primary={t(langInvestDateRangePicker.custom.tag)} />
								</ListItemButton>
							</ListItem>
							<ListItem disablePadding>
								<ListItemButton
									onClick={() => onItemAllSelected()}
									selected={itemDateSelected === 'all'}
								>
									<ListItemText primary={t(langInvestDateRangePicker.all.tag)} />
								</ListItemButton>
							</ListItem>
						</List>
					</Grid>
				</Grid>
				<Grid container item sm={9}>
					<Grid
						container
						item
						sm={12}
						sx={{
							'& .MuiPickersDay-root': {
								fontSize: '1em',
							},
							button: {
								color: isDarkMode ? '#F1F2F3' : '#0000008a',
							},
							'button:disabled': {
								color: isDarkMode ? '#0000008a' : '#F1F2F3',
							},
							'& .Mui-selected': {
								backgroundColor: isDarkMode ? '#2ABDBB !important' : '#1A2B48 !important',
							},
						}}
					>
						<Grid item sm={6}>
							<CalendarPicker
								date={tempStartDate}
								onChange={(newDate) => newDate && onSelectCustom('start', newDate)}
								minDate={minDate}
								maxDate={tempEndDate}
							/>
						</Grid>
						<Grid item sm={6}>
							<CalendarPicker
								date={tempEndDate}
								onChange={(newDate) => newDate && onSelectCustom('end', newDate)}
								maxDate={maxDate}
								minDate={tempStartDate}
							/>
						</Grid>
					</Grid>
					<Grid item sm={12} sx={{ textAlign: 'right' }}>
						<Button variant="text" onClick={onClose}>
							Annuler
						</Button>
						<Button variant="contained" color="secondary" onClick={onSubmit}>
							Valider
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</CAppPaper>
	);
};
type CDateRangePickerProps = {
	startDate: Moment;
	endDate: Moment;
	onChange: (newStartDate: Moment, newEndDate: Moment) => void;
	minDate: Moment;
	maxDate: Moment;
};

const CDateRangePicker = (props: CDateRangePickerProps) => {
	const { startDate, endDate, onChange, minDate, maxDate } = props;
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';

	const onClickDatePicker = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	const open = Boolean(anchorEl);
	return (
		<>
			<Grid
				container
				sx={{
					backgroundColor: isDarkMode ? '#2ABDBB80' : '#FFFFFF',
					padding: '5px 10px',
					alignItems: 'center',
					borderRadius: '5px',
				}}
			>
				<Grid item xs={11} sx={{ display: 'flex', textAlign: 'left' }}>
					<Grid item>{startDate.format('DD/MM/YYYY')}</Grid>
					<Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
						<ArrowRightIcon />
					</Grid>
					<Grid item>{endDate.format('DD/MM/YYYY')}</Grid>
				</Grid>
				<Grid item xs={1} sx={{ display: 'flex', justifyContent: 'right' }}>
					<IconButton aria-label="calendar" size="small" onClick={onClickDatePicker}>
						<CalendarIcon />
					</IconButton>
				</Grid>
			</Grid>

			<Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ zIndex: 11000 }}>
				<CDateRangePickerPopper
					startDate={startDate}
					endDate={endDate}
					onChange={onChange}
					minDate={minDate}
					maxDate={maxDate}
					onClose={onClickDatePicker}
				/>
			</Popper>
		</>
	);
};

export default CDateRangePicker;

const ArrowRightIcon = createSvgIcon(
	<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<g clipPath="url(#clip0_3358_2584)">
			<path d="M5 12H19" stroke="#1A2B48" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
			<path
				d="M13 6L19 12L13 18"
				stroke="#1A2B48"
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</g>
		<defs>
			<clipPath id="clip0_3358_2584">
				<rect width="24" height="24" fill="white" />
			</clipPath>
		</defs>
	</svg>,
	'ArrowRightIcon',
);

const CalendarIcon = createSvgIcon(
	<svg xmlns="http://www.w3.org/2000/svg">
		<path d="M7 11H9V13H7V11ZM7 15H9V17H7V15ZM11 11H13V13H11V11ZM11 15H13V17H11V15ZM15 11H17V13H15V11ZM15 15H17V17H15V15Z" />
		<path d="M5 22H19C20.103 22 21 21.103 21 20V6C21 4.897 20.103 4 19 4H17V2H15V4H9V2H7V4H5C3.897 4 3 4.897 3 6V20C3 21.103 3.897 22 5 22ZM19 8L19.001 20H5V8H19Z" />
	</svg>,
	'CalendarIcon',
);
